import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import Cookies from 'js-cookie';
import moment from 'moment';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import {
  LinearProgress,
  linearProgressClasses,
  ListItem,
  ListItemButton,
} from '@mui/material';
import styled from 'styled-components';
// eslint-disable-next-line import/no-extraneous-dependencies
import html2canvas from 'html2canvas';
// eslint-disable-next-line import/no-extraneous-dependencies
import { jsPDF } from 'jspdf';
import { APIGetMaintenanceRecordsByAssetId } from '../../../config/restAPI/FetchAPI';
import {
  APIPostMaintenanceReceiptOCR,
  APIPostMaintenance,
  APIPostMaintenanceAttachmentUploadUrl,
} from '../../../config/restAPI/PostAPI';
import { APIDeleteMaintenanceRecord } from '../../../config/restAPI/DeleteAPI';
import { APIPutMaintenanceRecord } from '../../../config/restAPI/PatchAPI';
import Title from '../../../component/title/Title';
import ListNavigationTab from '../../../component/listNavigationTab/ListNavigationTab';
import BreadCrumbs from '../../../component/breadcrumbs/BreadCrumbs';
import SnackBar from '../../../component/snackbar/Snackbar';
import { ExportCSVButton } from '../../../component/buttonComponent/ButtonComponent';
import DropDownList from '../../../component/iconDropDown/iconDropDown';
import TruckIcon from '../../../image/truckIcon.svg';
import { useApiData } from '../../../config/reducer/Modules';
import MaintenanceComplianceTab from './MaintenanceComplianceTab';
import MaintenanceComplianceHistoryTab from './MaintenanceComplianceHistoryTab';
import { isApiResponseNotFound, isApiResponseSuccess } from './maintenanceComplianceUtils';
import { fetchDeviceTotalMileageKmForAsset } from './maintenanceDeviceMileage';
import '../../../component/configurationTab/configurationTab.css';
import '../assetsDevice/AssetsDevice.css';
import './MaintenanceModule.css';

const BorderLinearProgress = styled(LinearProgress)(() => ({
  height: 16,
  borderRadius: 5,
  [`&.${linearProgressClasses.root}`]: {
    height: '8px',
  },
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: '#EAECF0',
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#365A6D',
  },
}));

const parseMaintenanceRecordsFromApiResult = (result) => {
  const isSuccess = result?.data?.status === 200 || result?.status === 200;
  if (!isSuccess) return [];
  const responseData = result?.data?.data ?? result?.data;
  let records = [];
  if (Array.isArray(responseData)) {
    records = responseData;
  } else if (responseData && typeof responseData === 'object') {
    records = responseData?.records
      || responseData?.items
      || responseData?.maintenanceRecords
      || responseData?.maintenance_records
      || responseData?.results
      || [];
    if (!Array.isArray(records) || records.length === 0) {
      const wrapped = responseData?.record
        || responseData?.result
        || responseData?.item
        || responseData?.maintenance
        || responseData?.maintenanceRecord
        || responseData?.payload;
      if (wrapped && typeof wrapped === 'object') {
        records = Array.isArray(wrapped) ? wrapped : [wrapped];
      } else {
        const hasReceiptInfo = Boolean(responseData?.receiptInfo || responseData?.serviceDetails);
        records = hasReceiptInfo ? [responseData] : [];
      }
    }
  }
  return Array.isArray(records) ? records : [];
};

const pickMaintenanceRecordId = (record) => {
  if (!record || typeof record !== 'object') return '';
  // eslint-disable-next-line dot-notation
  const mongoId = record?.['_id'];
  return (
    mongoId
    || record?.id
    || record?.oid
    || record?.recordId
    || record?.mongoId
    || record?.maintenanceId
    || record?.maintenanceRecordId
    || record?.insertedId
    || ''
  );
};

// Maintenance APIs may surface validation under `error.details`, `missingFields`, or `field` (shape varies by route/version).
const maintenanceSubmitErrorDetailText = (error) => {
  const errPayload = error?.response?.data?.error;
  const detailList = errPayload?.details || errPayload?.missingFields || errPayload?.field;
  return Array.isArray(detailList) ? detailList.join(', ') : '';
};

const normalizeAssetTypeRow = (device) => {
  if (!device) return device;
  const { assetType } = device;
  let normalized = [];
  if (Array.isArray(assetType)) {
    normalized = assetType;
  } else if (assetType != null && assetType !== '') {
    normalized = [String(assetType)];
  }
  return { ...device, assetType: normalized };
};

const formatAssetTypeLabel = (assetType) => {
  if (Array.isArray(assetType)) return assetType.filter(Boolean).join(', ') || '-';
  if (assetType == null || assetType === '') return '-';
  return String(assetType);
};

const formatDeviceLastUpdated = (value) => {
  if (value == null || value === '') return '-';
  if (typeof value === 'object') {
    if (!value.date) return '-';
    const combined = `${value.date} ${value.time ?? ''}`.trim();
    return combined.replace(/(\d)(am|pm)\b/gi, '$1 $2');
  }
  return String(value).replace(/(\d)(am|pm)\b/gi, '$1 $2');
};

const assetTypeToMakeString = (assetType) => {
  if (Array.isArray(assetType)) return assetType[0] || assetType.join(', ') || '';
  return assetType != null ? String(assetType) : '';
};

const MAINTENANCE_PAYMENT_METHOD_OPTIONS = [
  'Cash',
  'Credit / Debit Card',
  'Online Banking / FPX',
  'E-Wallet',
  'Apple Pay',
  'Google Pay',
  'Bank Transfer',
  'Buy Now Pay Later (BNPL)',
  'Cheque',
  'Other',
];

const MAINTENANCE_SERVICE_TYPE_OPTIONS = [
  'Regular Servicing',
  'Engine',
  'Cooling System',
  'Brake',
  'Tyre',
  'Wheels & Rims',
  'Alignment & Balancing',
  'Suspension',
  'Steering',
  'Transmission / Gearbox',
  'Clutch & Flywheel',
  'Axle & Driveshaft',
  'Fuel System',
  'Exhaust',
  'Battery & Charging',
  'Electrical & Lights',
  'Air Conditioning (AC)',
  'Sensors & Diagnostics',
  'Body & Paint',
  'Windscreen & Glass',
  'Interior & Upholstery',
  'Security & Locking',
  'Fluids & Lubricants',
  'Inspection Services',
  'Others',
];

const parsePaymentMethodForForm = (apiValue) => {
  const value = String(apiValue || '').trim();
  if (!value) {
    return { paymentMethod: '', paymentMethodOther: '' };
  }
  if (MAINTENANCE_PAYMENT_METHOD_OPTIONS.includes(value)) {
    return { paymentMethod: value, paymentMethodOther: '' };
  }
  if (value.toLowerCase() === 'other' || value.toLowerCase().startsWith('other:')) {
    const detail = value.replace(/^other:\s*/i, '').trim();
    return { paymentMethod: 'Other', paymentMethodOther: detail };
  }
  return { paymentMethod: 'Other', paymentMethodOther: value };
};

const buildMaintenanceDropdownOptions = (options, currentValue) => {
  const mapped = options.map((item) => ({ label: item, value: item }));
  if (currentValue && !options.includes(currentValue)) {
    return [{ label: currentValue, value: currentValue }, ...mapped];
  }
  return mapped;
};

const findMaintenanceDropdownValue = (options, currentValue) => {
  if (!currentValue) return null;
  return options.find((opt) => opt.value === currentValue) || { label: currentValue, value: currentValue };
};

const formatPaymentMethodForApi = (paymentMethod, paymentMethodOther) => {
  const method = String(paymentMethod || '').trim();
  if (!method) return '';
  if (method !== 'Other') return method;
  const detail = String(paymentMethodOther || '').trim();
  return detail ? `Other: ${detail}` : 'Other';
};

const MaintenanceUnitInput = ({
  id,
  type = 'number',
  value,
  onChange,
  unit,
  step,
  placeholder,
  disabled = false,
  unitPosition,
}) => {
  const resolvedUnitPosition = unitPosition || (unit === 'RM' ? 'prefix' : 'suffix');
  const unitLabel = (
    <span className={`maintenance-input-with-unit__label maintenance-input-with-unit__label--${resolvedUnitPosition}`}>
      {unit}
    </span>
  );

  return (
    <div className={`maintenance-input-with-unit maintenance-input-with-unit--${resolvedUnitPosition}`}>
      {resolvedUnitPosition === 'prefix' ? unitLabel : null}
      <input
        id={id}
        type={type}
        className="maintenance-input-with-unit__field"
        value={value}
        onChange={onChange}
        step={step}
        placeholder={placeholder}
        disabled={disabled}
      />
      {resolvedUnitPosition === 'suffix' ? unitLabel : null}
    </div>
  );
};

const MaintenanceModule = () => {
  const { apiResponse } = useApiData();
  const navigate = useNavigate();
  const location = useLocation();
  const token = Cookies.get('jwtToken');
  const agencyID = Cookies.get('agencyUUID');
  const [navigtionClick, setNavigtionClick, navigationState, setNavigationState] = useOutletContext();
  const [activeTab, setActiveTab] = useState('all');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showNewReport, setShowNewReport] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedReceiptFile, setSelectedReceiptFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  // Receipt OCR (maintenance /ocr-result): request state and raw text shown in the form preview.
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrError, setOcrError] = useState('');
  const [ocrRawText, setOcrRawText] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [editingReport, setEditingReport] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showExportSnack, setShowExportSnack] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const exportProgressIntervalRef = useRef(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [historyActionError, setHistoryActionError] = useState('');
  const [reportLogsLoading, setReportLogsLoading] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState('');
  const [pendingDeleteReport, setPendingDeleteReport] = useState(null);
  const [reportLogsError, setReportLogsError] = useState('');
  const [odometerDefaultsLoading, setOdometerDefaultsLoading] = useState(false);
  const imageInputRef = useRef(null);
  const reportPdfRef = useRef(null);
  const enteredWithAssetRef = useRef(false);
  const selectedAssetIdRef = useRef(null);

  /** Backend requires schedulingAndWarranty.serviceInterval; fixed value, not shown in the form. */
  const defaultServiceIntervalFallback = '5000 km or 6 months';

  const defaultFormData = {
    receiptNumber: '',
    receiptDate: moment().format('YYYY-MM-DD'),
    serviceDate: moment().format('YYYY-MM-DD'),
    paymentMethod: '',
    paymentMethodOther: '',
    receiptFileUrl: '',
    vendorName: '',
    vendorAddress: '',
    vendorContactNumber: '',
    vendorEmail: '',
    registrationNumber: '',
    odometerReading: '',
    engineHours: '',
    serviceType: '',
    jobDescription: '',
    labourHours: '',
    partsSubtotal: '',
    tax: '',
    discount: '',
    totalAmount: '',
    paidAmount: '',
    outstandingBalance: '',
    nextServiceDueDate: '',
    nextServiceDueMileage: '',
  };
  const [formData, setFormData] = useState(defaultFormData);
  const defaultPartItem = {
    partName: '',
    partNumber: '',
    brand: '',
    quantity: '',
    unitPrice: '',
  };
  const createPartItem = () => ({
    id: `part-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    ...defaultPartItem,
  });
  const [partsList, setPartsList] = useState([createPartItem()]);

  const reportChecklistFields = [
    { key: 'receiptNumber', label: 'Invoice / Receipt Number' },
    { key: 'receiptDate', label: 'Receipt Date' },
    { key: 'serviceDate', label: 'Service Date' },
    { key: 'paymentMethod', label: 'Payment Method' },
    { key: 'vendorName', label: 'Workshop Name' },
    { key: 'registrationNumber', label: 'Registration Number' },
    { key: 'odometerReading', label: 'Odometer' },
    { key: 'engineHours', label: 'Engine Hours' },
    { key: 'serviceType', label: 'Service Type' },
    { key: 'jobDescription', label: 'Description' },
    { key: 'labourHours', label: 'Labour Hours' },
    { key: 'nextServiceDueDate', label: 'Next Service Date' },
    { key: 'nextServiceDueMileage', label: 'Next Service Mileage' },
  ];
  /** Form submit validation + red asterisk (only these show *). */
  const MAINTENANCE_REQUIRED_FIELD_KEYS = new Set([
    'receiptNumber',
    'paymentMethod',
    'receiptDate',
    'serviceDate',
    'vendorName',
    'serviceType',
    'jobDescription',
  ]);
  const renderRequiredLabel = (label, fieldKey = null) => (
    <>
      {label}
      {fieldKey && MAINTENANCE_REQUIRED_FIELD_KEYS.has(fieldKey) && (
        <span className="maintenance-required-indicator">*</span>
      )}
    </>
  );

  const normalizeLabel = (value) => String(value || '').trim().toLowerCase().replace(/[^\w\s]/g, '');

  // OCR returns free-text labels (checklist items, raw "Label: value" lines). Map normalized labels to form field keys.
  const ocrLabelToKeyMap = {
    'invoice receipt number': 'receiptNumber',
    'invoice number': 'receiptNumber',
    'receipt number': 'receiptNumber',
    'receipt date': 'receiptDate',
    'service date': 'serviceDate',
    currency: 'currency',
    'payment method': 'paymentMethod',
    'receipt upload': 'receiptFileUrl',
    'receipt file url': 'receiptFileUrl',
    'workshop name': 'vendorName',
    address: 'vendorAddress',
    'contact details': 'vendorContactNumber',
    'contact number': 'vendorContactNumber',
    email: 'vendorEmail',
    'registration number': 'registrationNumber',
    odometer: 'odometerReading',
    'engine hours': 'engineHours',
    'service type': 'serviceType',
    description: 'jobDescription',
    'job description': 'jobDescription',
    'labour hours': 'labourHours',
    subtotal: 'partsSubtotal',
    tax: 'tax',
    discount: 'discount',
    total: 'totalAmount',
    'total amount': 'totalAmount',
    paid: 'paidAmount',
    balance: 'outstandingBalance',
    'next service date': 'nextServiceDueDate',
    'next service mileage': 'nextServiceDueMileage',
  };

  const mapLabelToFieldKey = (label) => {
    if (!label) return null;
    const normalizedLabel = normalizeLabel(label);
    return ocrLabelToKeyMap[normalizedLabel] || null;
  };

  const [reportLogs, setReportLogs] = useState([]);

  const mapMaintenanceRecordToReportLog = (record) => {
    const receiptInfo = record?.receiptInfo || {};
    const serviceDetails = record?.serviceDetails || {};
    const assetIdentification = record?.assetIdentification || {};
    const costBreakdown = record?.costBreakdown || {};
    const schedulingAndWarranty = record?.schedulingAndWarranty || {};
    const vendor = record?.vendor || {};
    const createdRaw = record?.createdAt
      || record?.created_at
      || receiptInfo?.serviceDate
      || receiptInfo?.receiptDate
      || moment().toISOString();

    const checklistFromApi = Array.isArray(serviceDetails?.checklist)
      ? serviceDetails.checklist.map((item) => ({
        label: item?.label || item?.key || '-',
        value: item?.value || '',
      }))
      : reportChecklistFields.map((item) => ({
        label: item.label,
        value: ({
          receiptNumber: receiptInfo?.receiptNumber,
          receiptDate: receiptInfo?.receiptDate,
          serviceDate: receiptInfo?.serviceDate,
          paymentMethod: receiptInfo?.paymentMethod,
          vendorName: vendor?.name,
          registrationNumber: assetIdentification?.vehicleRegNum || assetIdentification?.assetRegNum,
          odometerReading: assetIdentification?.odometerReading,
          engineHours: assetIdentification?.engineHours,
          serviceType: serviceDetails?.type,
          jobDescription: serviceDetails?.jobDescription,
          labourHours: serviceDetails?.labourHours,
          nextServiceDueDate: schedulingAndWarranty?.nextServiceDueDate,
          nextServiceDueMileage: schedulingAndWarranty?.nextServiceDueMileage,
        }[item.key] || '-'),
      }));

    // eslint-disable-next-line dot-notation
    const mongoRecordId = record?.['_id'];

    const displayId = record?.id || record?.oid || record?.recordId || record?.mongoId || mongoRecordId || `r-${Date.now()}`;
    const deleteSourceId = mongoRecordId
      || record?.recordId
      || record?.mongoId
      || record?.maintenanceId
      || record?.maintenanceOid
      || record?.maintenanceRecordId
      || record?.oid
      || record?.id
      || '';

    return {
      id: displayId,
      sourceId: deleteSourceId,
      label: serviceDetails?.jobDescription || receiptInfo?.receiptNumber || serviceDetails?.reportTitle || record?.title || 'Untitled Report',
      createdAt: moment(createdRaw).format('DD MMM YYYY h:mm:ssa [GMT]ZZ'),
      checklist: checklistFromApi,
      remark: serviceDetails?.jobDescription || serviceDetails?.remark || '',
      rawRecord: record,
    };
  };

  const handleFormChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const applyCreateFormDefaults = async (assetId, { preserveExisting = false } = {}) => {
    const defaultNextServiceDate = moment().add(6, 'months').format('YYYY-MM-DD');
    let odometer = '';
    let nextServiceMileage = '';

    if (assetId && token && agencyID) {
      setOdometerDefaultsLoading(true);
      try {
        const totalMileageKm = await fetchDeviceTotalMileageKmForAsset(
          token,
          agencyID,
          assetId,
          selectedAsset?.mileageSource,
        );
        if (assetId !== selectedAssetIdRef.current) return;
        if (totalMileageKm !== null) {
          odometer = String(totalMileageKm);
          nextServiceMileage = String(totalMileageKm + 5000);
        }
      } catch {
        // Telemetry may be unavailable; user can still enter values manually.
      } finally {
        setOdometerDefaultsLoading(false);
      }
    }

    if (assetId !== selectedAssetIdRef.current) return;

    setFormData((prev) => ({
      ...prev,
      odometerReading: (!preserveExisting || !String(prev.odometerReading || '').trim())
        ? (odometer || prev.odometerReading)
        : prev.odometerReading,
      nextServiceDueDate: (!preserveExisting || !prev.nextServiceDueDate)
        ? defaultNextServiceDate
        : prev.nextServiceDueDate,
      nextServiceDueMileage: (!preserveExisting || !String(prev.nextServiceDueMileage || '').trim())
        ? (nextServiceMileage || prev.nextServiceDueMileage)
        : prev.nextServiceDueMileage,
    }));
  };

  const openCreateMaintenanceForm = async () => {
    setEditingReport(null);
    setSubmitError('');
    setOcrRawText('');
    setSelectedReceiptFile(null);
    setPartsList([createPartItem()]);
    setFormData({
      ...defaultFormData,
      registrationNumber: selectedAsset?.assetName || selectedAsset?.deviceSN || '',
    });
    setShowNewReport(false);
    setShowManualForm(true);
    await applyCreateFormDefaults(selectedAsset?.id);
  };

  const extractMaintenanceRecordId = (result) => {
    const parsed = parseMaintenanceRecordsFromApiResult(result);
    for (let i = 0; i < parsed.length; i += 1) {
      const id = pickMaintenanceRecordId(parsed[i]);
      if (id) return id;
    }
    const responseData = result?.data?.data ?? result?.data;
    return pickMaintenanceRecordId(responseData);
  };

  const findCreatedReportIdFromReports = (reports, payload) => {
    if (!Array.isArray(reports) || reports.length === 0) return '';
    const targetReceipt = String(payload?.receiptInfo?.receiptNumber || '').trim().toLowerCase();
    const targetDescription = String(payload?.serviceDetails?.jobDescription || '').trim().toLowerCase();
    const targetServiceDate = payload?.receiptInfo?.serviceDate
      ? moment(payload.receiptInfo.serviceDate).format('YYYY-MM-DD')
      : '';

    const matchedReport = reports.find((report) => {
      const rawRecord = report?.rawRecord || {};
      const receiptInfo = rawRecord?.receiptInfo || {};
      const serviceDetails = rawRecord?.serviceDetails || {};
      const recordReceipt = String(receiptInfo?.receiptNumber || '').trim().toLowerCase();
      const recordDescription = String(serviceDetails?.jobDescription || '').trim().toLowerCase();
      const recordServiceDate = receiptInfo?.serviceDate
        ? moment(receiptInfo.serviceDate).format('YYYY-MM-DD')
        : '';
      const isReceiptMatch = targetReceipt && recordReceipt === targetReceipt;
      const isDescriptionMatch = targetDescription && recordDescription === targetDescription;
      const isDateMatch = targetServiceDate && recordServiceDate === targetServiceDate;
      return isReceiptMatch || (isDescriptionMatch && isDateMatch);
    });

    return matchedReport?.sourceId || matchedReport?.id || '';
  };

  const uploadReceiptFileAndGetObjectKey = async (maintenanceId, file) => {
    const payload = {
      filename: file?.name || `receipt-${Date.now()}`,
      contentType: file?.type || 'application/octet-stream',
      contentLength: file?.size || 0,
    };
    const initResult = await APIPostMaintenanceAttachmentUploadUrl(token, agencyID, maintenanceId, payload);
    const initSuccess = initResult?.data?.status === 200 || initResult?.status === 200;
    if (!initSuccess) {
      const message = initResult?.response?.data?.message || initResult?.data?.message || 'Failed to initialize receipt upload.';
      throw new Error(message);
    }

    const initData = initResult?.data?.data || initResult?.data || {};
    const uploadUrl = initData?.uploadUrl;
    const objectKey = initData?.objectKey;
    if (!uploadUrl || !objectKey) {
      throw new Error('Invalid upload URL response for receipt file.');
    }

    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file?.type || 'application/octet-stream',
      },
      body: file,
    });
    if (!uploadResponse.ok) {
      throw new Error(`Receipt upload failed (${uploadResponse.status}).`);
    }

    return objectKey;
  };

  const handlePartChange = (partId, key, value) => {
    setPartsList((prev) => prev.map((item) => (item.id === partId ? { ...item, [key]: value } : item)));
  };

  const handleAddPartRow = () => {
    setPartsList((prev) => [...prev, createPartItem()]);
  };

  const handleRemovePartRow = (partId) => {
    setPartsList((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((item) => item.id !== partId);
    });
  };

  const toNumber = (value, fallback = 0) => {
    if (value === '' || value === null || typeof value === 'undefined') return fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  /**
   * API rejects empty strings for several optional fields; send a placeholder when the user leaves them blank.
   */
  const maintenanceOptionalApiString = (value) => {
    const s = String(value ?? '').trim();
    return s.length ? s : '-';
  };

  const maintenanceOptionalEmailApi = (value) => {
    const s = String(value ?? '').trim();
    return s.length ? s : 'n/a@example.com';
  };

  const stripMaintenancePlaceholderString = (value, placeholder = '-') => {
    const s = String(value ?? '').trim();
    return s === placeholder ? '' : s;
  };

  const stripMaintenancePlaceholderEmail = (value) => {
    const s = String(value ?? '').trim();
    return s === 'n/a@example.com' ? '' : s;
  };

  const buildMaintenancePayload = () => {
    const cleanParts = partsList.filter((part) => (
      part.partName || part.partNumber || part.brand || part.quantity || part.unitPrice
    ));
    const partsSubtotalFromRows = cleanParts.reduce(
      (acc, part) => acc + (toNumber(part.quantity, 0) * toNumber(part.unitPrice, 0)),
      0,
    );
    const primaryPart = cleanParts[0] || defaultPartItem;
    const partsSubtotalValue = toNumber(formData.partsSubtotal, partsSubtotalFromRows);
    const taxValue = toNumber(formData.tax, 0);
    const discountValue = toNumber(formData.discount, 0);
    const totalAmountValue = toNumber(formData.totalAmount, partsSubtotalValue + taxValue - discountValue);
    const paidAmountValue = toNumber(formData.paidAmount, totalAmountValue);
    const outstandingBalanceValue = toNumber(formData.outstandingBalance, Math.max(totalAmountValue - paidAmountValue, 0));

    return {
      receiptInfo: {
        receiptNumber: formData.receiptNumber || '',
        receiptDate: formData.receiptDate ? moment(formData.receiptDate).toISOString() : moment().toISOString(),
        serviceDate: formData.serviceDate ? moment(formData.serviceDate).toISOString() : moment().toISOString(),
        currency: 'MYR',
        paymentMethod: formatPaymentMethodForApi(formData.paymentMethod, formData.paymentMethodOther) || 'Cash',
        receiptFileUrl: formData.receiptFileUrl || '',
      },
      assetIdentification: {
        assetId: selectedAsset?.id || '',
        assetRegNum: formData.registrationNumber || selectedAsset?.assetName || selectedAsset?.deviceSN || '',
        vehicleRegNum: formData.registrationNumber || selectedAsset?.assetName || selectedAsset?.deviceSN || '',
        vin: '',
        make: assetTypeToMakeString(selectedAsset?.assetType) || '',
        odometerReading: formData.odometerReading || '0',
        engineHours: formData.engineHours || '0',
      },
      costBreakdown: {
        partsSubtotal: partsSubtotalValue,
        labourSubtotal: 0,
        tax: taxValue,
        discount: discountValue,
        totalAmount: totalAmountValue,
        paidAmount: paidAmountValue,
        outstandingBalance: outstandingBalanceValue,
      },
      partsAndConsumables: {
        partName: maintenanceOptionalApiString(primaryPart.partName),
        partNumber: maintenanceOptionalApiString(primaryPart.partNumber),
        brand: maintenanceOptionalApiString(primaryPart.brand),
        quantity: toNumber(primaryPart.quantity, 0),
        unitPrice: toNumber(primaryPart.unitPrice, 0),
        totalPrice: toNumber(primaryPart.quantity, 0) * toNumber(primaryPart.unitPrice, 0),
      },
      schedulingAndWarranty: {
        serviceInterval: defaultServiceIntervalFallback,
        nextServiceDueDate: formData.nextServiceDueDate
          ? moment(formData.nextServiceDueDate).toISOString()
          : moment().add(6, 'months').toISOString(),
        nextServiceDueMileage: toNumber(formData.nextServiceDueMileage, 0),
      },
      serviceDetails: {
        type: formData.serviceType || '',
        jobDescription: formData.jobDescription || '',
        labourHours: toNumber(formData.labourHours, 0),
      },
      systemMetadata: {
        source: 'web_ui',
        submittedAt: moment().toISOString(),
        verificationStatus: 'pending',
      },
      vendor: {
        name: formData.vendorName || '',
        address: maintenanceOptionalApiString(formData.vendorAddress),
        contactNumber: maintenanceOptionalApiString(formData.vendorContactNumber),
        email: maintenanceOptionalEmailApi(formData.vendorEmail),
      },
    };
  };

  async function fetchMaintenanceReportLogs(assetId) {
    if (!assetId) {
      setReportLogs([]);
      return [];
    }
    setReportLogsLoading(true);
    try {
      const result = await APIGetMaintenanceRecordsByAssetId(() => {}, token, agencyID, assetId);
      if (assetId !== selectedAssetIdRef.current) return [];
      if (isApiResponseNotFound(result)) {
        if (assetId === selectedAssetIdRef.current) {
          setReportLogs([]);
          setReportLogsError('');
        }
        return [];
      }
      if (!isApiResponseSuccess(result)) {
        if (assetId === selectedAssetIdRef.current) {
          setReportLogs([]);
          setReportLogsError('Failed to fetch maintenance reports.');
        }
        return [];
      }
      const responseData = result?.data?.data ?? result?.data;
      let records = [];
      if (Array.isArray(responseData)) {
        records = responseData;
      } else if (responseData && typeof responseData === 'object') {
        records = responseData?.records
          || responseData?.items
          || responseData?.maintenanceRecords
          || responseData?.maintenance_records
          || responseData?.results
          || [];
        // Handle API shape where `data` is a single maintenance object.
        if (!Array.isArray(records) || records.length === 0) {
          const hasReceiptInfo = Boolean(responseData?.receiptInfo || responseData?.serviceDetails);
          records = hasReceiptInfo ? [responseData] : [];
        }
      }
      if (assetId !== selectedAssetIdRef.current) return [];
      const mapped = records.map(mapMaintenanceRecordToReportLog);
      setReportLogs(mapped);
      setReportLogsError('');
      return mapped;
    } catch (error) {
      if (assetId === selectedAssetIdRef.current) {
        setReportLogs([]);
        setReportLogsError('Failed to fetch maintenance reports.');
      }
      return [];
    } finally {
      if (assetId === selectedAssetIdRef.current) {
        setReportLogsLoading(false);
      }
    }
  }

  const handleDeleteReport = async () => {
    const report = pendingDeleteReport;
    const recordId = report?.sourceId || report?.id;
    if (!recordId) return;

    setReportLogsError('');
    setDeleteLoadingId(recordId);
    try {
      const result = await APIDeleteMaintenanceRecord(token, () => {}, agencyID, recordId);
      const isSuccess = result?.data?.status === 200 || result?.status === 200;
      if (!isSuccess) {
        const message = result?.response?.data?.message || result?.data?.message || 'Failed to delete maintenance report.';
        setReportLogsError(message);
      } else {
        if (selectedReport?.id === report?.id) {
          setSelectedReport(null);
          setActiveTab('all');
        }
        await fetchMaintenanceReportLogs(selectedAsset?.id);
      }
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to delete maintenance report.';
      setReportLogsError(message);
    } finally {
      setDeleteLoadingId('');
      setPendingDeleteReport(null);
    }
  };

  const mapRecordToFormState = (record = {}) => {
    const receiptInfo = record?.receiptInfo || {};
    const serviceDetails = record?.serviceDetails || {};
    const assetIdentification = record?.assetIdentification || {};
    const costBreakdown = record?.costBreakdown || {};
    const schedulingAndWarranty = record?.schedulingAndWarranty || {};
    const vendor = record?.vendor || {};
    const part = record?.partsAndConsumables || {};

    const nextFormData = {
      ...defaultFormData,
      receiptNumber: receiptInfo?.receiptNumber || '',
      receiptDate: receiptInfo?.receiptDate ? moment(receiptInfo.receiptDate).format('YYYY-MM-DD') : defaultFormData.receiptDate,
      serviceDate: receiptInfo?.serviceDate ? moment(receiptInfo.serviceDate).format('YYYY-MM-DD') : defaultFormData.serviceDate,
      ...parsePaymentMethodForForm(receiptInfo?.paymentMethod),
      receiptFileUrl: receiptInfo?.receiptFileUrl || '',
      vendorName: vendor?.name || '',
      vendorAddress: stripMaintenancePlaceholderString(vendor?.address),
      vendorContactNumber: stripMaintenancePlaceholderString(vendor?.contactNumber),
      vendorEmail: stripMaintenancePlaceholderEmail(vendor?.email),
      registrationNumber: assetIdentification?.vehicleRegNum || assetIdentification?.assetRegNum || selectedAsset?.assetName || selectedAsset?.deviceSN || '',
      odometerReading: assetIdentification?.odometerReading ?? '',
      engineHours: assetIdentification?.engineHours ?? '',
      serviceType: serviceDetails?.type || '',
      jobDescription: serviceDetails?.jobDescription || '',
      labourHours: serviceDetails?.labourHours ?? '',
      partsSubtotal: costBreakdown?.partsSubtotal ?? '',
      tax: costBreakdown?.tax ?? '',
      discount: costBreakdown?.discount ?? '',
      totalAmount: costBreakdown?.totalAmount ?? '',
      paidAmount: costBreakdown?.paidAmount ?? '',
      outstandingBalance: costBreakdown?.outstandingBalance ?? '',
      nextServiceDueDate: schedulingAndWarranty?.nextServiceDueDate
        ? moment(schedulingAndWarranty.nextServiceDueDate).format('YYYY-MM-DD')
        : '',
      nextServiceDueMileage: schedulingAndWarranty?.nextServiceDueMileage ?? '',
    };

    const nextPartsList = [{
      id: `part-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      partName: stripMaintenancePlaceholderString(part?.partName),
      partNumber: stripMaintenancePlaceholderString(part?.partNumber),
      brand: stripMaintenancePlaceholderString(part?.brand),
      quantity: part?.quantity ?? '',
      unitPrice: part?.unitPrice ?? '',
    }];

    return { nextFormData, nextPartsList };
  };

  const handleUpdateReportClick = () => {
    if (!selectedReport?.rawRecord) return;
    const { nextFormData, nextPartsList } = mapRecordToFormState(selectedReport.rawRecord);
    setEditingReport(selectedReport);
    setFormData(nextFormData);
    setPartsList(nextPartsList);
    setSubmitError('');
    setOcrRawText('');
    setShowManualForm(true);
  };

  const clearExportProgressInterval = () => {
    if (exportProgressIntervalRef.current) {
      clearInterval(exportProgressIntervalRef.current);
      exportProgressIntervalRef.current = null;
    }
  };

  const setShowExportSnackWrapper = (value) => {
    if (value === false) {
      clearExportProgressInterval();
      setExportProgress(0);
    }
    setShowExportSnack(value);
  };

  const handleDownloadReport = async () => {
    if (!selectedReport) return;
    if (!reportPdfRef.current) {
      setHistoryActionError('Unable to generate report PDF.');
      return;
    }

    setHistoryActionError('');
    setShowExportSnack(true);
    setExportProgress(0);
    setDownloadLoading(true);
    setIsExportingPdf(true);
    clearExportProgressInterval();
    exportProgressIntervalRef.current = setInterval(() => {
      setExportProgress((prev) => (prev >= 88 ? prev : prev + 11));
    }, 200);

    try {
      await new Promise((resolve) => {
        requestAnimationFrame(() => resolve());
      });
      const canvas = await html2canvas(reportPdfRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });
      const imgData = canvas.toDataURL('image/png');
      // eslint-disable-next-line new-cap
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const safeLabel = String(selectedReport?.label || 'maintenance-report')
        .replace(/[^a-zA-Z0-9-_ ]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .toLowerCase();
      pdf.save(`${safeLabel || 'maintenance-report'}.pdf`);
      setExportProgress(100);
      setTimeout(() => {
        setShowExportSnack(false);
        setExportProgress(0);
      }, 700);
    } catch (error) {
      setHistoryActionError('Unable to download report PDF. Please try again.');
      setShowExportSnack(false);
      setExportProgress(0);
    } finally {
      clearExportProgressInterval();
      setIsExportingPdf(false);
      setDownloadLoading(false);
    }
  };

  useEffect(() => () => {
    if (exportProgressIntervalRef.current) {
      clearInterval(exportProgressIntervalRef.current);
    }
  }, []);

  const handleFormSubmit = async () => {
    const missing = [];
    if (!String(formData.receiptNumber || '').trim()) missing.push('Invoice / Receipt Number');
    if (!String(formData.paymentMethod || '').trim()) missing.push('Payment Method');
    if (formData.paymentMethod === 'Other' && !String(formData.paymentMethodOther || '').trim()) {
      missing.push('Payment Method (Other)');
    }
    if (!formData.receiptDate) missing.push('Receipt Date');
    if (!formData.serviceDate) missing.push('Service Date');
    if (!String(formData.vendorName || '').trim()) missing.push('Workshop Name');
    if (!String(formData.serviceType || '').trim()) missing.push('Service Type');
    if (!String(formData.jobDescription || '').trim()) missing.push('Description');
    if (missing.length) {
      setSubmitError(`Please fill required fields: ${missing.join(', ')}.`);
      return;
    }
    if (!selectedAsset) {
      setSubmitError('Please select an asset before submitting.');
      return;
    }

    setSubmitError('');
    try {
      let payload = buildMaintenancePayload();
      const editingRecordId = editingReport?.sourceId || editingReport?.id;
      let result = null;
      let refreshedReports = [];

      if (editingRecordId) {
        if (selectedReceiptFile) {
          const objectKey = await uploadReceiptFileAndGetObjectKey(editingRecordId, selectedReceiptFile);
          payload = {
            ...payload,
            receiptInfo: {
              ...payload.receiptInfo,
              receiptFileUrl: objectKey,
            },
          };
        }
        result = await APIPutMaintenanceRecord(token, setSubmitLoading, agencyID, editingRecordId, payload);
      } else {
        result = await APIPostMaintenance(setSubmitLoading, token, agencyID, payload);
      }
      const isSuccess = result?.data?.status === 200 || result?.status === 200;
      if (!isSuccess) {
        const message = result?.response?.data?.message
          || result?.data?.message
          || (editingRecordId ? 'Failed to update maintenance report.' : 'Failed to create maintenance report.');
        const details = Array.isArray(result?.data?.data) ? result.data.data.join(', ') : '';
        setSubmitError(details ? `${message} (${details})` : message);
        return;
      }

      if (!editingRecordId && selectedReceiptFile) {
        let createdRecordId = extractMaintenanceRecordId(result);
        if (!createdRecordId) {
          refreshedReports = await fetchMaintenanceReportLogs(selectedAsset?.id);
          createdRecordId = findCreatedReportIdFromReports(refreshedReports, payload);
        }
        if (!createdRecordId) {
          setSubmitError('Report created, but unable to link receipt file (missing maintenance ID).');
          return;
        }
        let objectKey = '';
        try {
          objectKey = await uploadReceiptFileAndGetObjectKey(createdRecordId, selectedReceiptFile);
        } catch (attachmentError) {
          setSubmitError(`Report created, but receipt upload failed: ${attachmentError?.message || 'Unable to upload receipt file.'}`);
          return;
        }
        const updateReceiptResult = await APIPutMaintenanceRecord(
          token,
          setSubmitLoading,
          agencyID,
          createdRecordId,
          {
            receiptInfo: {
              receiptFileUrl: objectKey,
            },
          },
        );
        const isUpdateReceiptSuccess = updateReceiptResult?.data?.status === 200 || updateReceiptResult?.status === 200;
        if (!isUpdateReceiptSuccess) {
          const message = updateReceiptResult?.response?.data?.message
            || updateReceiptResult?.data?.message
            || 'Report created, but failed to save receipt file URL.';
          setSubmitError(message);
          return;
        }
      }

      if (!Array.isArray(refreshedReports) || refreshedReports.length === 0) {
        refreshedReports = await fetchMaintenanceReportLogs(selectedAsset?.id);
      }
      if (editingRecordId && selectedReport) {
        const updatedSelectedReport = refreshedReports.find((report) => (
          report.sourceId === editingRecordId || report.id === editingRecordId
        ));
        if (updatedSelectedReport) {
          setSelectedReport(updatedSelectedReport);
        }
      }
      setShowManualForm(false);
      setShowNewReport(false);
      setEditingReport(null);
      setFormData({
        ...defaultFormData,
        registrationNumber: selectedAsset?.assetName || selectedAsset?.deviceSN || '',
      });
      setPartsList([createPartItem()]);
      setOcrRawText('');
      setSelectedReceiptFile(null);
    } catch (error) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;
      const detailText = maintenanceSubmitErrorDetailText(error);
      if (status) {
        setSubmitError(`Create report failed (${status})${message ? `: ${message}` : ''}${detailText ? ` (${detailText})` : ''}`);
      } else if (error?.message) {
        setSubmitError(error.message);
      } else {
        setSubmitError('Unable to create maintenance report. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleFormClose = () => {
    setShowManualForm(false);
    setEditingReport(null);
    setFormData({
      ...defaultFormData,
      registrationNumber: selectedAsset?.assetName || selectedAsset?.deviceSN || '',
    });
    setPartsList([createPartItem()]);
    setOcrRawText('');
    setSubmitError('');
    setSelectedReceiptFile(null);
  };

  // Maintenance receipt OCR does not guarantee one response shape: the API may send flat `fields`,
  // nested sections (receiptInfo, vendor, …), checklist arrays, and/or raw text. We merge those
  // paths so the form still prefills; a stable OCR contract would simplify this.
  const prefillFormFromOCRResponse = (responsePayload, fileName) => {
    const initialPrefill = { ...defaultFormData };
    let prefilledPartsList = [createPartItem()];
    const payload = responsePayload?.data || responsePayload || {};
    const fieldsObject = payload?.fields || payload?.details || {};
    const checklistArray = payload?.checklist || payload?.items || [];
    const rawText = payload?.rawText || payload?.text || '';

    Object.keys(fieldsObject).forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(initialPrefill, key)) {
        initialPrefill[key] = fieldsObject[key] ?? '';
      }
    });

    if (Array.isArray(checklistArray)) {
      checklistArray.forEach((item) => {
        const mappedKey = mapLabelToFieldKey(item?.label || item?.name || item?.field);
        if (mappedKey) {
          initialPrefill[mappedKey] = item?.value ?? '';
        }
      });
    }

    if (rawText && typeof rawText === 'string') {
      rawText.split('\n').forEach((line) => {
        const [label, ...rest] = line.split(':');
        if (!label || rest.length === 0) return;
        const mappedKey = mapLabelToFieldKey(label);
        if (mappedKey && !initialPrefill[mappedKey]) {
          initialPrefill[mappedKey] = rest.join(':').trim();
        }
      });
    }

    initialPrefill.receiptNumber = initialPrefill.receiptNumber
      || payload?.receiptInfo?.receiptNumber
      || payload?.reportTitle
      || payload?.title
      || fileName?.replace(/\.[^/.]+$/, '')
      || '';

    initialPrefill.jobDescription = initialPrefill.jobDescription
      || payload?.serviceDetails?.jobDescription
      || payload?.remark
      || payload?.notes
      || '';

    if (payload?.receiptInfo) {
      initialPrefill.receiptDate = payload.receiptInfo.receiptDate
        ? moment(payload.receiptInfo.receiptDate).format('YYYY-MM-DD')
        : initialPrefill.receiptDate;
      initialPrefill.serviceDate = payload.receiptInfo.serviceDate
        ? moment(payload.receiptInfo.serviceDate).format('YYYY-MM-DD')
        : initialPrefill.serviceDate;
      const parsedPaymentMethod = parsePaymentMethodForForm(payload.receiptInfo.paymentMethod);
      initialPrefill.paymentMethod = parsedPaymentMethod.paymentMethod || initialPrefill.paymentMethod;
      initialPrefill.paymentMethodOther = parsedPaymentMethod.paymentMethodOther || initialPrefill.paymentMethodOther;
      initialPrefill.receiptFileUrl = payload.receiptInfo.receiptFileUrl || initialPrefill.receiptFileUrl;
    }
    if (payload?.assetIdentification) {
      initialPrefill.registrationNumber = payload.assetIdentification.vehicleRegNum
        || payload.assetIdentification.assetRegNum
        || initialPrefill.registrationNumber;
      initialPrefill.odometerReading = payload.assetIdentification.odometerReading || initialPrefill.odometerReading;
      initialPrefill.engineHours = payload.assetIdentification.engineHours || initialPrefill.engineHours;
    }
    if (payload?.serviceDetails) {
      initialPrefill.serviceType = payload.serviceDetails.type || initialPrefill.serviceType;
      initialPrefill.labourHours = payload.serviceDetails.labourHours || initialPrefill.labourHours;
    }
    if (payload?.costBreakdown) {
      initialPrefill.partsSubtotal = payload.costBreakdown.partsSubtotal ?? initialPrefill.partsSubtotal;
      initialPrefill.tax = payload.costBreakdown.tax ?? initialPrefill.tax;
      initialPrefill.discount = payload.costBreakdown.discount ?? initialPrefill.discount;
      initialPrefill.totalAmount = payload.costBreakdown.totalAmount ?? initialPrefill.totalAmount;
      initialPrefill.paidAmount = payload.costBreakdown.paidAmount ?? initialPrefill.paidAmount;
      initialPrefill.outstandingBalance = payload.costBreakdown.outstandingBalance ?? initialPrefill.outstandingBalance;
    }
    if (payload?.schedulingAndWarranty) {
      initialPrefill.nextServiceDueDate = payload.schedulingAndWarranty.nextServiceDueDate
        ? moment(payload.schedulingAndWarranty.nextServiceDueDate).format('YYYY-MM-DD')
        : initialPrefill.nextServiceDueDate;
      initialPrefill.nextServiceDueMileage = payload.schedulingAndWarranty.nextServiceDueMileage
        || initialPrefill.nextServiceDueMileage;
    }
    if (payload?.vendor) {
      initialPrefill.vendorName = payload.vendor.name || initialPrefill.vendorName;
      initialPrefill.vendorAddress = payload.vendor.address || initialPrefill.vendorAddress;
      initialPrefill.vendorContactNumber = payload.vendor.contactNumber || initialPrefill.vendorContactNumber;
      initialPrefill.vendorEmail = payload.vendor.email || initialPrefill.vendorEmail;
    }
    if (payload?.partsAndConsumables) {
      const part = payload.partsAndConsumables;
      prefilledPartsList = [{
        id: `part-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        partName: part.partName || '',
        partNumber: part.partNumber || '',
        brand: part.brand || '',
        quantity: part.quantity ?? '',
        unitPrice: part.unitPrice ?? '',
      }];
    }

    return {
      prefilledFormData: initialPrefill,
      prefilledPartsList,
      rawText,
    };
  };

  // POSTs receipt image to maintenance OCR; success body is passed to prefillFormFromOCRResponse.
  const handleProcessReceiptOCR = async () => {
    if (!selectedImage || !selectedAsset) {
      setOcrError('Please select an asset and image first.');
      return;
    }

    setOcrError('');
    try {
      const payload = new FormData();
      // POST /maintenance/ocr-result: single file part name used by the API (avoid duplicating the same file under two keys).
      payload.append('receiptImage', selectedImage);
      payload.append('assetId', selectedAsset.id || '');
      payload.append('assetName', selectedAsset.assetName || selectedAsset.deviceSN || '');

      const result = await APIPostMaintenanceReceiptOCR(setOcrLoading, token, agencyID, payload);
      const isSuccess = result?.data?.status === 200 || result?.status === 200;
      if (!isSuccess) {
        setOcrError('Unable to recognize receipt text. Please try again.');
        return;
      }

      const { prefilledFormData, prefilledPartsList, rawText } = prefillFormFromOCRResponse(result?.data, selectedImage?.name);
      setEditingReport(null);
      setFormData(prefilledFormData);
      setPartsList(prefilledPartsList);
      setOcrRawText(rawText || '');
      setSelectedReceiptFile(selectedImage);
      setShowNewReport(false);
      setShowManualForm(true);
      await applyCreateFormDefaults(selectedAsset?.id, { preserveExisting: true });
      setSelectedImage(null);
      if (imageInputRef.current) imageInputRef.current.value = '';
    } catch (error) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;
      if (status) {
        setOcrError(`OCR request failed (${status})${message ? `: ${message}` : ''}`);
      } else {
        setOcrError('OCR processing failed. Please try again or create report manually.');
      }
    } finally {
      setOcrLoading(false);
    }
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setActiveTab('unread');
  };

  const inventoryNavigationList = [
    {
      text: 'Assets & Devices',
      value: 1,
      path: '/inventory/assetdevices',
      enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner' || apiResponse?.role === 'System Administrator')
        ? true
        : apiResponse?.module?.fleetManagement?.asset,
    },
    {
      text: 'Connectivity',
      value: 4,
      path: '/inventory/connectivity',
      enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner')
        ? true
        : apiResponse?.module?.fleetManagement?.connectivity,
    },
    {
      text: 'Battery Configurator',
      value: 5,
      path: '/inventory/batteryconfigurator',
      enabled: apiResponse?.role === 'System Administrator',
    },
    {
      text: 'Vehicle Configurator',
      value: 6,
      path: '/inventory/vehicleconfigurator',
      enabled: apiResponse?.role === 'System Administrator',
    },
    {
      text: 'Fuel Configurator',
      value: 8,
      path: '/inventory/fuelconfigurator',
      enabled: apiResponse?.role === 'System Administrator',
    },
  ];

  const handleSelectAsset = useCallback((device) => {
    setSelectedAsset(normalizeAssetTypeRow(device));
    setSelectedReport(null);
    setFormData((prev) => ({
      ...prev,
      registrationNumber: device.assetName || device.deviceSN || '',
    }));
  }, []);

  useEffect(() => {
    const raw = location.state;
    if (raw?.preselectedAsset || raw?.id) {
      enteredWithAssetRef.current = true;
      return;
    }
    if (!enteredWithAssetRef.current) {
      navigate('/inventory/assetdevices', { replace: true });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    const raw = location.state;
    const fromRow = raw?.preselectedAsset;
    const fromAssetScreen = raw?.id
      ? {
        id: raw.id,
        deviceSN: raw.deviceSN,
        assetName: raw.assetName,
        assetType: raw.assetType,
        lastUpdated: raw.lastUpdated && typeof raw.lastUpdated === 'object' && raw.lastUpdated?.date
          ? `${raw.lastUpdated.date} ${raw.lastUpdated.time ?? ''}`.trim()
          : raw.lastUpdated,
      }
      : null;
    const preselected = fromRow || fromAssetScreen;
    if (!preselected?.id) return;
    enteredWithAssetRef.current = true;
    handleSelectAsset(preselected);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, handleSelectAsset, navigate]);

  useEffect(() => {
    setNavigtionClick(1);
    setNavigationState('inventory');
  }, [setNavigtionClick, setNavigationState]);

  useEffect(() => {
    selectedAssetIdRef.current = selectedAsset?.id ?? null;
    if (!token || !selectedAsset?.id) {
      setReportLogs([]);
      return;
    }
    fetchMaintenanceReportLogs(selectedAsset.id);
  }, [token, selectedAsset?.id]);

  const hasDisplayValue = (value) => {
    if (value === null || typeof value === 'undefined') return false;
    if (typeof value === 'number') return Number.isFinite(value);
    if (typeof value === 'string') return value.trim() !== '';
    return true;
  };

  const formatDisplayDate = (value) => {
    if (!hasDisplayValue(value)) return '';
    const parsed = moment(value);
    if (!parsed.isValid()) return String(value);
    return parsed.format('DD/MM/YYYY');
  };

  const formatDisplayNumber = (value) => {
    if (!hasDisplayValue(value)) return '';
    if (typeof value === 'number') return String(value);
    const parsed = Number(value);
    return Number.isFinite(parsed) ? String(parsed) : String(value);
  };

  const formatDisplayCurrency = (value) => {
    if (!hasDisplayValue(value)) return '';
    return `RM ${formatDisplayNumber(value)}`;
  };

  const formatDisplayDistance = (value) => {
    if (!hasDisplayValue(value)) return '';
    return `${formatDisplayNumber(value)} KM`;
  };

  const buildReportHistorySections = (report) => {
    const rawRecord = report?.rawRecord || {};
    const receiptInfo = rawRecord?.receiptInfo || {};
    const vendor = rawRecord?.vendor || {};
    const assetIdentification = rawRecord?.assetIdentification || {};
    const serviceDetails = rawRecord?.serviceDetails || {};
    const part = rawRecord?.partsAndConsumables || {};
    const cost = rawRecord?.costBreakdown || {};
    const scheduling = rawRecord?.schedulingAndWarranty || {};

    const sections = [
      {
        title: 'Receipt Information',
        rows: [
          { label: 'Invoice', value: receiptInfo?.receiptNumber },
          { label: 'Receipt Date', value: formatDisplayDate(receiptInfo?.receiptDate) },
          { label: 'Service Date', value: formatDisplayDate(receiptInfo?.serviceDate) },
          { label: 'Payment Method', value: receiptInfo?.paymentMethod },
          // { label: 'Receipt Upload', value: receiptInfo?.receiptFileUrl },
        ],
      },
      {
        title: 'Vendor',
        rows: [
          { label: 'Workshop Name', value: vendor?.name },
          { label: 'Address', value: vendor?.address },
          { label: 'Contact Details', value: vendor?.contactNumber },
          { label: 'Email', value: vendor?.email },
        ],
      },
      {
        title: 'Asset Info',
        rows: [
          { label: 'Registration Number', value: assetIdentification?.vehicleRegNum || assetIdentification?.assetRegNum },
          { label: 'Odometer', value: formatDisplayDistance(assetIdentification?.odometerReading) },
          { label: 'Engine Hours', value: formatDisplayNumber(assetIdentification?.engineHours) },
        ],
      },
      {
        title: 'Service Info',
        rows: [
          { label: 'Service Type', value: serviceDetails?.type },
          { label: 'Description', value: serviceDetails?.jobDescription },
          { label: 'Labour Hours', value: formatDisplayNumber(serviceDetails?.labourHours) },
        ],
      },
      {
        title: 'Parts',
        rows: [
          { label: 'Part Name', value: part?.partName },
          { label: 'Part Number', value: part?.partNumber },
          { label: 'Brand', value: part?.brand },
          { label: 'Quantity', value: formatDisplayNumber(part?.quantity) },
          { label: 'Price', value: formatDisplayCurrency(part?.unitPrice) },
        ],
      },
      {
        title: 'Cost',
        rows: [
          { label: 'Subtotal', value: formatDisplayCurrency(cost?.partsSubtotal) },
          { label: 'Labour Subtotal', value: formatDisplayCurrency(cost?.labourSubtotal) },
          { label: 'Tax', value: formatDisplayCurrency(cost?.tax) },
          { label: 'Discount', value: formatDisplayCurrency(cost?.discount) },
          { label: 'Total', value: formatDisplayCurrency(cost?.totalAmount) },
          { label: 'Paid', value: formatDisplayCurrency(cost?.paidAmount) },
          { label: 'Balance', value: formatDisplayCurrency(cost?.outstandingBalance) },
        ],
      },
      {
        title: 'Scheduling',
        rows: [
          { label: 'Next Service Date', value: formatDisplayDate(scheduling?.nextServiceDueDate) },
          { label: 'Next Service Mileage', value: formatDisplayDistance(scheduling?.nextServiceDueMileage) },
          {
            label: 'Current mileage',
            value: hasDisplayValue(assetIdentification?.odometerReading)
              ? formatDisplayDistance(assetIdentification?.odometerReading)
              : '-',
            hintRow: true,
          },
        ],
      },
    ];

    return sections
      .map((section) => ({
        ...section,
        rows: section.rows.filter((row) => hasDisplayValue(row.value)),
      }))
      .filter((section) => section.rows.length > 0);
  };

  const submitButtonLabel = (() => {
    if (submitLoading && editingReport) return 'Updating...';
    if (submitLoading) return 'Submitting...';
    if (editingReport) return 'Update Report';
    return 'Submit Report';
  })();

  const renderAllTab = () => (
    <div className="maintenance-all-tab">
      <div className="maintenance-detail-card">
        <div className="maintenance-detail-row">
          <span className="maintenance-detail-label">Asset Name</span>
          <span className="maintenance-detail-value">
            {selectedAsset.assetName || selectedAsset.deviceSN}
          </span>
        </div>
        <div className="maintenance-detail-row">
          <span className="maintenance-detail-label">Asset Type</span>
          <span className="maintenance-detail-value">{formatAssetTypeLabel(selectedAsset.assetType)}</span>
        </div>
        <div className="maintenance-detail-row">
          <span className="maintenance-detail-label">Device last Update</span>
          <span className="maintenance-detail-value">{formatDeviceLastUpdated(selectedAsset.lastUpdated)}</span>
        </div>
      </div>
      <div className="maintenance-report-logs">
        <div className="maintenance-report-logs-header">
          <h3 className="maintenance-report-logs-title">Report Logs</h3>
          <button
            type="button"
            className="maintenance-new-report-btn"
            onClick={() => {
              setOcrError('');
              setShowNewReport(!showNewReport);
            }}
          >
            + New Report
          </button>
        </div>
        <div className="maintenance-report-logs-list">
          {reportLogsError && (
            <div className="maintenance-report-log-item">
              <span className="maintenance-report-log-label maintenance-report-log-error">{reportLogsError}</span>
            </div>
          )}
          {reportLogsLoading && (
            <div className="maintenance-report-log-item">
              <span className="maintenance-report-log-label">Loading report logs...</span>
            </div>
          )}
          {!reportLogsLoading && reportLogs.length === 0 && (
            <div className="maintenance-report-log-item">
              <span className="maintenance-report-log-label">No reports found for this asset</span>
            </div>
          )}
          {reportLogs.map((report) => (
            <div className="maintenance-report-log-item" key={report.id}>
              <span className="maintenance-report-log-label">{report.label}</span>
              <div className="maintenance-report-log-actions">
                <button
                  type="button"
                  className="maintenance-report-log-view"
                  onClick={() => handleViewReport(report)}
                >
                  View
                </button>
                <button
                  type="button"
                  className="maintenance-report-log-delete"
                  onClick={() => setPendingDeleteReport(report)}
                  disabled={deleteLoadingId === (report?.sourceId || report?.id)}
                >
                  {deleteLoadingId === (report?.sourceId || report?.id) ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderUnreadTab = () => {
    if (!selectedAsset) {
      return (
        <div className="maintenance-unread-tab">
          <div className="maintenance-checklist-empty">Loading…</div>
        </div>
      );
    }

    const reportSections = buildReportHistorySections(selectedReport);

    if (!selectedReport) {
      return (
        <div className="maintenance-unread-tab">
          <div className="maintenance-checklist-empty">
            Select a report from the Assets tab to view its history.
          </div>
        </div>
      );
    }

    return (
      <div className="maintenance-unread-tab">
        <div className="maintenance-checklist-card" ref={reportPdfRef}>
          <h3 className="maintenance-checklist-title">
            {selectedReport?.label || 'Maintenance report'}
          </h3>
          <div className="maintenance-checklist-info">
            <div className="maintenance-checklist-row">
              <span className="maintenance-checklist-label">Asset Name :</span>
              <span className="maintenance-checklist-value">
                {selectedAsset.assetName || selectedAsset.deviceSN}
              </span>
            </div>
            <div className="maintenance-checklist-row">
              <span className="maintenance-checklist-label">Asset Type :</span>
              <span className="maintenance-checklist-value">
                {formatAssetTypeLabel(selectedAsset.assetType)}
              </span>
            </div>
            <div className="maintenance-checklist-row">
              <span className="maintenance-checklist-label">Report Last Update :</span>
              <span className="maintenance-checklist-value">
                {selectedReport?.createdAt || selectedAsset.lastUpdated}
              </span>
            </div>
          </div>
          {reportSections.map((section) => (
            <React.Fragment key={section.title}>
              <div className="maintenance-checklist-divider" />
              <h4 className="maintenance-form-section-title">{section.title}</h4>
              <div className="maintenance-checklist-items">
                {section.rows.map((item) => (
                  <div
                    className={`maintenance-checklist-row${item.hintRow ? ' maintenance-checklist-row--hint' : ''}`}
                    key={`${section.title}-${item.label}`}
                  >
                    <>
                      <span className="maintenance-checklist-label">
                        {item.label}
                        {' :'}
                      </span>
                      <span className="maintenance-checklist-value">
                        {item.value}
                      </span>
                    </>
                  </div>
                ))}
              </div>
            </React.Fragment>
          ))}
        {selectedReport && (
          <div className="maintenance-history-actions">
            <button
              type="button"
              className="maintenance-history-update-btn"
              onClick={handleUpdateReportClick}
              disabled={downloadLoading}
            >
              Update
            </button>
          </div>
        )}
        {historyActionError && (
          <div className="maintenance-history-action-error">{historyActionError}</div>
        )}
        </div>
      </div>
    );
  };

  const maintenanceBreadcrumbSeparator = [
    <img loading="lazy" key="bc-truck" src={TruckIcon} alt="live" style={{ display: 'flex', alignItems: 'center' }} />,
    <ListItemButton
      key="bc-assets"
      disableRipple
      disableGutters
      onClick={() => navigate('/inventory/assetdevices', { replace: true })}
      style={{ backgroundColor: 'transparent' }}
      sx={{
        display: 'flex',
        color: '#475467',
        fontSize: '14px',
        fontWeight: 500,
        fontFamily: 'Inter',
        fontStyle: 'normal',
      }}
    >
      <div>Assets & Devices</div>
    </ListItemButton>,
    <ListItem
      key="bc-mm"
      sx={{
        padding: 0,
        color: '#475467',
        fontSize: '14px',
        fontWeight: 500,
        fontFamily: 'Inter',
        fontStyle: 'normal',
      }}
    >
      Maintenance Module
    </ListItem>,
  ];

  return (
    <div className="maintenance-container">
      <ListNavigationTab
        list={inventoryNavigationList}
        navigtionClick={navigtionClick}
        setNavigtionClick={setNavigtionClick}
      />
      <div className="maintenance-header">
        <BreadCrumbs customSeperator={maintenanceBreadcrumbSeparator} />
        <Title
          title="Maintenance Module"
          subtitle="View and manage asset maintenance records and checklists"
          titleSize="1.875rem"
        />
        <div className="maintenance-tabs">
          <div className="maintenance-tabs-inner">
            <div className="config-tab-button-group">
              <button
                type="button"
                className={`maintenance-tab ${activeTab === 'all' ? 'maintenance-tab-active' : ''}`}
                onClick={() => setActiveTab('all')}
              >
                <div className="config-tab-text">Assets</div>
              </button>
              <button
                type="button"
                className={`maintenance-tab ${activeTab === 'unread' ? 'maintenance-tab-active' : ''}`}
                onClick={() => setActiveTab('unread')}
              >
                <div className="config-tab-text">Report History</div>
              </button>
              <button
                type="button"
                className={`maintenance-tab ${activeTab === 'compliance' ? 'maintenance-tab-active' : ''}`}
                onClick={() => setActiveTab('compliance')}
              >
                <div className="config-tab-text">Compliance</div>
              </button>
              <button
                type="button"
                className={`maintenance-tab ${activeTab === 'complianceHistory' ? 'maintenance-tab-active' : ''}`}
                onClick={() => setActiveTab('complianceHistory')}
              >
                <div className="config-tab-text">Compliance History</div>
              </button>
            </div>
            {activeTab === 'unread' && selectedAsset && selectedReport && (
              <div className="maintenance-header-download">
                <ExportCSVButton
                  label={downloadLoading ? 'Downloading...' : 'Download Report'}
                  buttonHeight="40px"
                  disable={downloadLoading}
                  onClickFunction={handleDownloadReport}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="maintenance-body">
        {!selectedAsset && (
          <div className="maintenance-all-tab">
            <div className="maintenance-checklist-empty">Loading…</div>
          </div>
        )}
        {selectedAsset && activeTab === 'all' && renderAllTab()}
        {selectedAsset && activeTab === 'unread' && renderUnreadTab()}
        {selectedAsset && activeTab === 'compliance' && (
          <MaintenanceComplianceTab
            selectedAsset={selectedAsset}
            token={token}
            agencyID={agencyID}
            formatAssetTypeLabel={formatAssetTypeLabel}
            deviceLastUpdated={formatDeviceLastUpdated(selectedAsset.lastUpdated)}
            reportLogs={reportLogs}
            reportLogsLoading={reportLogsLoading}
          />
        )}
        {selectedAsset && activeTab === 'complianceHistory' && (
          <MaintenanceComplianceHistoryTab
            selectedAsset={selectedAsset}
            token={token}
            agencyID={agencyID}
          />
        )}
      </div>
      {pendingDeleteReport && (
        <div className="maintenance-modal-overlay">
          <div className="maintenance-delete-confirm-modal">
            <div className="maintenance-delete-confirm-header">
              <h3 className="maintenance-delete-confirm-title">Delete Report</h3>
              <button
                type="button"
                className="maintenance-modal-close"
                onClick={() => setPendingDeleteReport(null)}
              >
                &times;
              </button>
            </div>
            <p className="maintenance-delete-confirm-text">
              Are you sure want to delete
              {' '}
              <strong>{pendingDeleteReport?.label || '-'}</strong>
              {' '}
              report?
            </p>
            <div className="maintenance-delete-confirm-actions">
              <button
                type="button"
                className="maintenance-delete-cancel-btn"
                onClick={() => setPendingDeleteReport(null)}
                disabled={deleteLoadingId === (pendingDeleteReport?.sourceId || pendingDeleteReport?.id)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="maintenance-delete-confirm-btn"
                onClick={handleDeleteReport}
                disabled={deleteLoadingId === (pendingDeleteReport?.sourceId || pendingDeleteReport?.id)}
              >
                {deleteLoadingId === (pendingDeleteReport?.sourceId || pendingDeleteReport?.id) ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showNewReport && (
        <div
          className="maintenance-modal-overlay"
          onClick={() => {
            setShowNewReport(false);
            setSelectedImage(null);
            setOcrError('');
          }}
          role="presentation"
        >
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            className="maintenance-new-report-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="maintenance-modal-header">
              <h3 className="maintenance-modal-title">Create New Report</h3>
              <button
                type="button"
                className="maintenance-modal-close"
                onClick={() => {
                  setShowNewReport(false);
                  setSelectedImage(null);
                  setOcrError('');
                }}
              >
                &times;
              </button>
            </div>
            <div className="maintenance-new-report-modal-body">
              {/* <p className="maintenance-new-report-subtitle">
                Choose how you would like to create a maintenance report
              </p> */}
              <div className="maintenance-new-report-options">
                <button
                  type="button"
                  className="maintenance-new-report-option-primary"
                  onClick={openCreateMaintenanceForm}
                >
                  Create report manually
                </button>
                {/*
                  Temporarily disable OCR/image upload entry point.
                  Keep only "Create report manually" available.
                <div className="maintenance-new-report-divider-text">or</div>
                <div
                  className={`maintenance-image-dropzone${isDragging ? ' dragging' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    const file = e.dataTransfer.files[0];
                    if (file && file.type.startsWith('image/')) {
                      setSelectedImage(file);
                    }
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    ref={imageInputRef}
                    className="maintenance-image-input-hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) setSelectedImage(file);
                    }}
                  />
                  <button
                    type="button"
                    className="maintenance-image-select-btn"
                    onClick={() => imageInputRef.current.click()}
                  >
                    Select image file
                  </button>
                  <span className="maintenance-image-drop-text">
                    or drop image here
                  </span>
                  {selectedImage && (
                    <div className="maintenance-image-selected">
                      <span className="maintenance-image-filename">
                        {selectedImage.name}
                      </span>
                      <button
                        type="button"
                        className="maintenance-image-remove"
                        onClick={() => {
                          setSelectedImage(null);
                          imageInputRef.current.value = '';
                        }}
                      >
                        &times;
                      </button>
                    </div>
                  )}
                  {!selectedAsset && (
                    <span className="maintenance-image-warning">
                      Select an asset first before OCR processing.
                    </span>
                  )}
                  {ocrError && (
                    <span className="maintenance-image-error">{ocrError}</span>
                  )}
                  <button
                    type="button"
                    className="maintenance-image-process-btn"
                    onClick={handleProcessReceiptOCR}
                    disabled={!selectedImage || !selectedAsset || ocrLoading}
                  >
                    {ocrLoading ? 'Processing receipt...' : 'Extract receipt details'}
                  </button>
                </div>
                */}
              </div>
            </div>
          </div>
        </div>
      )}
      {showManualForm && (
        <div className="maintenance-modal-overlay">
          <div className="maintenance-modal">
            <div className="maintenance-modal-header">
              <h3 className="maintenance-modal-title">{editingReport ? 'Update Maintenance Report' : 'Create Maintenance Report'}</h3>
              <button
                type="button"
                className="maintenance-modal-close"
                onClick={handleFormClose}
              >
                &times;
              </button>
            </div>
            {/* eslint-disable jsx-a11y/label-has-associated-control */}
            <div className="maintenance-modal-body">
              {selectedAsset && (
                <div className="maintenance-form-asset-info">
                  <span>
                    Asset:
                    {' '}
                    <strong>{selectedAsset.assetName || selectedAsset.deviceSN}</strong>
                  </span>
                  <span>
                    Type:
                    {' '}
                    <strong>{formatAssetTypeLabel(selectedAsset.assetType)}</strong>
                  </span>
                </div>
              )}
              {ocrRawText && (
                <div className="maintenance-ocr-preview">
                  <span className="maintenance-form-label">Captured Receipt Text (editable values below)</span>
                  <div className="maintenance-ocr-preview-text">{ocrRawText}</div>
                </div>
              )}
              <div className="maintenance-form-divider" />
              <h4 className="maintenance-form-section-title">Receipt Information</h4>
              <div className="maintenance-form-grid">
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="receiptNumber">{renderRequiredLabel('Invoice / Receipt Number', 'receiptNumber')}</label>
                  <input id="receiptNumber" type="text" className="maintenance-form-input" value={formData.receiptNumber} onChange={(e) => handleFormChange('receiptNumber', e.target.value)} />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="receiptDate">{renderRequiredLabel('Receipt Date', 'receiptDate')}</label>
                  <input id="receiptDate" type="date" className="maintenance-form-input" value={formData.receiptDate} onChange={(e) => handleFormChange('receiptDate', e.target.value)} />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="serviceDate">{renderRequiredLabel('Service Date', 'serviceDate')}</label>
                  <input id="serviceDate" type="date" className="maintenance-form-input" value={formData.serviceDate} onChange={(e) => handleFormChange('serviceDate', e.target.value)} />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="paymentMethod">{renderRequiredLabel('Payment Method', 'paymentMethod')}</label>
                  <DropDownList
                    placeholder="Select payment method"
                    width="100%"
                    menuPortal
                    selectedValue={findMaintenanceDropdownValue(
                      buildMaintenanceDropdownOptions(MAINTENANCE_PAYMENT_METHOD_OPTIONS, formData.paymentMethod),
                      formData.paymentMethod,
                    )}
                    setSelectedValue={(option) => {
                      const value = option?.value || '';
                      setFormData((prev) => ({
                        ...prev,
                        paymentMethod: value,
                        paymentMethodOther: value === 'Other' ? prev.paymentMethodOther : '',
                      }));
                    }}
                    options={buildMaintenanceDropdownOptions(MAINTENANCE_PAYMENT_METHOD_OPTIONS, formData.paymentMethod)}
                  />
                </div>
                {formData.paymentMethod === 'Other' && (
                  <div className="maintenance-form-group">
                    <label className="maintenance-form-label" htmlFor="paymentMethodOther">
                      Please specify payment method
                      <span className="maintenance-required-indicator">*</span>
                    </label>
                    <input
                      id="paymentMethodOther"
                      type="text"
                      className="maintenance-form-input"
                      value={formData.paymentMethodOther}
                      onChange={(e) => handleFormChange('paymentMethodOther', e.target.value)}
                      placeholder="Enter payment method"
                    />
                  </div>
                )}
                {/* <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="receiptFileUrl">{renderRequiredLabel('Receipt Upload')}</label>
                  <input
                    id="receiptFileUrl"
                    type="file"
                    accept="application/pdf,image/*"
                    className="maintenance-form-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setSelectedReceiptFile(file);
                      if (!file && formData.receiptFileUrl) {
                        handleFormChange('receiptFileUrl', '');
                      }
                    }}
                  />
                  {selectedReceiptFile && (
                    <span className="maintenance-receipt-file-name">{selectedReceiptFile.name}</span>
                  )}
                  {!selectedReceiptFile && formData.receiptFileUrl && (
                    <span className="maintenance-receipt-file-name">{formData.receiptFileUrl}</span>
                  )}
                </div> */}
              </div>
              <div className="maintenance-form-grid">
                <div className="maintenance-form-group maintenance-form-full">
                  <h4 className="maintenance-form-section-title">Vendor</h4>
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="vendorName">{renderRequiredLabel('Workshop Name', 'vendorName')}</label>
                  <input id="vendorName" type="text" className="maintenance-form-input" value={formData.vendorName} onChange={(e) => handleFormChange('vendorName', e.target.value)} />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="vendorContactNumber">{renderRequiredLabel('Contact Details')}</label>
                  <input id="vendorContactNumber" type="text" className="maintenance-form-input" value={formData.vendorContactNumber} onChange={(e) => handleFormChange('vendorContactNumber', e.target.value)} />
                </div>
                <div className="maintenance-form-group maintenance-form-full">
                  <label className="maintenance-form-label" htmlFor="vendorAddress">{renderRequiredLabel('Address')}</label>
                  <input id="vendorAddress" type="text" className="maintenance-form-input" value={formData.vendorAddress} onChange={(e) => handleFormChange('vendorAddress', e.target.value)} />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="vendorEmail">{renderRequiredLabel('Email')}</label>
                  <input id="vendorEmail" type="text" className="maintenance-form-input" value={formData.vendorEmail} onChange={(e) => handleFormChange('vendorEmail', e.target.value)} />
                </div>
              </div>
              <div className="maintenance-form-divider" />
              <h4 className="maintenance-form-section-title">Asset Info</h4>
              <div className="maintenance-form-grid">
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="registrationNumber">{renderRequiredLabel('Registration Number (Auto-filled)')}</label>
                  <input
                    id="registrationNumber"
                    type="text"
                    className="maintenance-form-input maintenance-form-input--readonly"
                    value={formData.registrationNumber}
                    disabled
                    readOnly
                  />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="odometerReading">{renderRequiredLabel('Odometer')}</label>
                  <MaintenanceUnitInput
                    id="odometerReading"
                    value={formData.odometerReading}
                    onChange={(e) => handleFormChange('odometerReading', e.target.value)}
                    unit="KM"
                    placeholder={odometerDefaultsLoading && !formData.odometerReading ? 'Loading…' : undefined}
                  />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="engineHours">{renderRequiredLabel('Engine Hours')}</label>
                  <input id="engineHours" type="number" className="maintenance-form-input" value={formData.engineHours} onChange={(e) => handleFormChange('engineHours', e.target.value)} />
                </div>
              </div>
              <div className="maintenance-form-divider" />
              <h4 className="maintenance-form-section-title">Service Info</h4>
              <div className="maintenance-form-grid">
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="serviceType">{renderRequiredLabel('Service Type', 'serviceType')}</label>
                  <DropDownList
                    placeholder="Select service type"
                    width="100%"
                    menuPortal
                    selectedValue={findMaintenanceDropdownValue(
                      buildMaintenanceDropdownOptions(MAINTENANCE_SERVICE_TYPE_OPTIONS, formData.serviceType),
                      formData.serviceType,
                    )}
                    setSelectedValue={(option) => handleFormChange('serviceType', option?.value || '')}
                    options={buildMaintenanceDropdownOptions(MAINTENANCE_SERVICE_TYPE_OPTIONS, formData.serviceType)}
                  />
                </div>
                <div className="maintenance-form-group maintenance-form-full">
                  <label className="maintenance-form-label" htmlFor="jobDescription">{renderRequiredLabel('Description', 'jobDescription')}</label>
                  <textarea id="jobDescription" className="maintenance-form-textarea" rows={3} value={formData.jobDescription} onChange={(e) => handleFormChange('jobDescription', e.target.value)} />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="labourHours">{renderRequiredLabel('Labour Hours')}</label>
                  <input id="labourHours" type="number" step="0.1" className="maintenance-form-input" value={formData.labourHours} onChange={(e) => handleFormChange('labourHours', e.target.value)} />
                </div>
              </div>
              <div className="maintenance-form-divider" />
              <div className="maintenance-part-header">
                <h4 className="maintenance-form-section-title">Parts Section</h4>
                <button type="button" className="maintenance-part-add-btn" onClick={handleAddPartRow}>+ Add Part</button>
              </div>
              {partsList.map((part) => (
                <div className="maintenance-form-grid maintenance-part-row" key={part.id}>
                  <div className="maintenance-form-group">
                    <label className="maintenance-form-label" htmlFor={`partName-${part.id}`}>{renderRequiredLabel('Part Name')}</label>
                    <input id={`partName-${part.id}`} type="text" className="maintenance-form-input" value={part.partName} onChange={(e) => handlePartChange(part.id, 'partName', e.target.value)} />
                  </div>
                  <div className="maintenance-form-group">
                    <label className="maintenance-form-label" htmlFor={`partNumber-${part.id}`}>{renderRequiredLabel('Part Number')}</label>
                    <input id={`partNumber-${part.id}`} type="text" className="maintenance-form-input" value={part.partNumber} onChange={(e) => handlePartChange(part.id, 'partNumber', e.target.value)} />
                  </div>
                  <div className="maintenance-form-group">
                    <label className="maintenance-form-label" htmlFor={`partBrand-${part.id}`}>{renderRequiredLabel('Brand')}</label>
                    <input id={`partBrand-${part.id}`} type="text" className="maintenance-form-input" value={part.brand} onChange={(e) => handlePartChange(part.id, 'brand', e.target.value)} />
                  </div>
                  <div className="maintenance-form-group">
                    <label className="maintenance-form-label" htmlFor={`partQuantity-${part.id}`}>{renderRequiredLabel('Quantity')}</label>
                    <input id={`partQuantity-${part.id}`} type="number" className="maintenance-form-input" value={part.quantity} onChange={(e) => handlePartChange(part.id, 'quantity', e.target.value)} />
                  </div>
                  <div className="maintenance-form-group">
                    <label className="maintenance-form-label" htmlFor={`partUnitPrice-${part.id}`}>{renderRequiredLabel('Price')}</label>
                    <MaintenanceUnitInput
                      id={`partUnitPrice-${part.id}`}
                      value={part.unitPrice}
                      onChange={(e) => handlePartChange(part.id, 'unitPrice', e.target.value)}
                      unit="RM"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="maintenance-form-group maintenance-part-remove-wrap">
                    <button type="button" className="maintenance-part-remove-btn" onClick={() => handleRemovePartRow(part.id)} disabled={partsList.length === 1}>Remove</button>
                  </div>
                </div>
              ))}
              <div className="maintenance-form-divider" />
              <h4 className="maintenance-form-section-title">Cost Section</h4>
              <div className="maintenance-form-grid">
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="partsSubtotal">{renderRequiredLabel('Subtotal')}</label>
                  <MaintenanceUnitInput
                    id="partsSubtotal"
                    value={formData.partsSubtotal}
                    onChange={(e) => handleFormChange('partsSubtotal', e.target.value)}
                    unit="RM"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="tax">{renderRequiredLabel('Tax')}</label>
                  <MaintenanceUnitInput
                    id="tax"
                    value={formData.tax}
                    onChange={(e) => handleFormChange('tax', e.target.value)}
                    unit="RM"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="discount">{renderRequiredLabel('Discount')}</label>
                  <MaintenanceUnitInput
                    id="discount"
                    value={formData.discount}
                    onChange={(e) => handleFormChange('discount', e.target.value)}
                    unit="RM"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="totalAmount">{renderRequiredLabel('Total')}</label>
                  <MaintenanceUnitInput
                    id="totalAmount"
                    value={formData.totalAmount}
                    onChange={(e) => handleFormChange('totalAmount', e.target.value)}
                    unit="RM"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="paidAmount">{renderRequiredLabel('Paid')}</label>
                  <MaintenanceUnitInput
                    id="paidAmount"
                    value={formData.paidAmount}
                    onChange={(e) => handleFormChange('paidAmount', e.target.value)}
                    unit="RM"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="outstandingBalance">{renderRequiredLabel('Balance')}</label>
                  <MaintenanceUnitInput
                    id="outstandingBalance"
                    value={formData.outstandingBalance}
                    onChange={(e) => handleFormChange('outstandingBalance', e.target.value)}
                    unit="RM"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="maintenance-form-divider" />
              <h4 className="maintenance-form-section-title">Scheduling</h4>
              <div className="maintenance-form-grid">
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="nextServiceDueDate">{renderRequiredLabel('Next Service Date')}</label>
                  <input id="nextServiceDueDate" type="date" className="maintenance-form-input" value={formData.nextServiceDueDate} onChange={(e) => handleFormChange('nextServiceDueDate', e.target.value)} />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label" htmlFor="nextServiceDueMileage">{renderRequiredLabel('Next Service Mileage')}</label>
                  <MaintenanceUnitInput
                    id="nextServiceDueMileage"
                    value={formData.nextServiceDueMileage}
                    onChange={(e) => handleFormChange('nextServiceDueMileage', e.target.value)}
                    unit="KM"
                  />
                  <span className="maintenance-current-mileage-hint">
                    Current mileage:
                    {' '}
                    {hasDisplayValue(formData.odometerReading) ? `${formData.odometerReading} KM` : '-'}
                  </span>
                </div>
              </div>
              {submitError && (
                <div className="maintenance-submit-error">{submitError}</div>
              )}
            </div>
            {/* eslint-enable jsx-a11y/label-has-associated-control */}
            <div className="maintenance-modal-footer">
              <button
                type="button"
                className="maintenance-modal-cancel"
                onClick={handleFormClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="maintenance-modal-submit"
                onClick={handleFormSubmit}
                disabled={submitLoading}
              >
                {submitButtonLabel}
              </button>
            </div>
          </div>
        </div>
      )}
      {showExportSnack && (
        <div className="maintenance-export-notification-wrap">
          <SnackBar
            state={showExportSnack}
            setState={setShowExportSnackWrapper}
            title="Exporting Data"
            deleteSubtitle
            module="TelemetryLog"
            action="info"
          >
            <div className="maintenance-export-notification-body">
              Your download will begin shortly
              <div className="maintenance-export-progress-bar">
                <BorderLinearProgress variant="determinate" value={exportProgress} style={{ width: '280px' }} />
                <div className="maintenance-export-progress-bar-text">
                  {exportProgress}
                  % processed...
                </div>
              </div>
            </div>
          </SnackBar>
        </div>
      )}
    </div>
  );
};

export default MaintenanceModule;
